import { motion } from "framer-motion";
import { Crosshair, CalendarCheck, TimerOff } from "lucide-react";

const DOMAINS = [
  "Healthcare",
  "CRM",
  "Mortgage",
  "Logistics",
  "Insurance",
  "Credit Bureaus",
];

const VALUES = [
  {
    icon: Crosshair,
    title: "Precision",
    copy: "Measured, tested, verified. We ship nothing we cannot prove.",
  },
  {
    icon: CalendarCheck,
    title: "Predictability",
    copy: "Fixed scope, fixed quality — no surprises at 3 a.m.",
  },
  {
    icon: TimerOff,
    title: "Zero Downtime",
    copy: "Resilience engineered in from the first commit, not patched on.",
  },
];

const EASE = [0.16, 1, 0.3, 1];

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.7, ease: EASE, delay },
});

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="scroll-mt-24 bg-paper py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-6">
            <motion.p
              {...reveal()}
              className="mb-5 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-brand"
            >
              <span className="h-px w-10 bg-brand" />
              About JISLL
            </motion.p>
            <motion.h2
              {...reveal(0.05)}
              className="font-heading text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-[2.75rem] lg:leading-[1.12]"
            >
              Engineering you can set your watch by.
            </motion.h2>
            <motion.div {...reveal(0.12)} className="mt-7 space-y-5 text-base leading-relaxed text-slate-600 sm:text-lg">
              <p>
                JISLL Technology Services Pvt Ltd is a software engineering
                and IT consulting startup built around a single obsession:
                systems that hold. We build focused products — from end-to-end
                outpatient healthcare to KYC and KYB onboarding — and take on
                consulting engagements where quality is non-negotiable.
              </p>
              <p>
                We are a young company — but our leadership isn't. Our
                founders bring around 14 years of enterprise architecture
                experience across Healthcare, CRM, Mortgage, Logistics,
                Insurance and Credit Bureaus. The same rigour that ran those
                systems now runs yours. That is why our tagline is also our
                promise.
              </p>
            </motion.div>

            <motion.div
              {...reveal(0.2)}
              className="mt-10 overflow-hidden rounded-3xl rounded-tr-[3.5rem] border border-slate-200/70"
            >
              <img
                src="https://images.unsplash.com/photo-1765371514743-45bd8e6c0a28?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
                alt="Inside a JISLL delivery office"
                className="aspect-[16/9] w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                loading="lazy"
              />
            </motion.div>
          </div>

          <div className="lg:col-span-6">
            <motion.div
              {...reveal(0.1)}
              data-testid="leadership-panel"
              className="flex flex-col rounded-3xl border border-slate-200/80 bg-white p-8 transition-all duration-200 hover:border-brand/40 sm:p-10"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">
                Leadership
              </p>
              <p className="mt-4 font-heading text-5xl font-extrabold tracking-tight text-ink sm:text-6xl">
                14+ <span className="text-2xl font-bold sm:text-3xl">years</span>
              </p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">
                Enterprise architecture experience
              </p>
              <p className="mt-5 text-sm leading-relaxed text-slate-600 sm:text-base">
                Architecture decisions made under real load, in regulated
                industries, at enterprise scale — now applied to products and
                engagements of every size.
              </p>
              <div
                className="mt-8 flex flex-wrap gap-2"
                data-testid="domain-expertise-chips"
              >
                {DOMAINS.map((d) => (
                  <span
                    key={d}
                    className="rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-600"
                  >
                    {d}
                  </span>
                ))}
              </div>
            </motion.div>

            <div className="mt-4 space-y-4">
              {VALUES.map((v, i) => (
                <motion.div
                  key={v.title}
                  {...reveal(0.08 * i)}
                  className="flex items-start gap-5 rounded-3xl border border-slate-200/80 bg-white p-7 transition-colors duration-200 hover:border-brand/40"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-50 text-ink">
                    <v.icon className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="flex items-baseline gap-3">
                      <span className="font-mono text-[10px] tracking-[0.2em] text-brand">
                        0{i + 1}
                      </span>
                      <span className="font-heading text-lg font-bold text-ink">
                        {v.title}
                      </span>
                    </span>
                    <span className="mt-1.5 block text-sm leading-relaxed text-slate-600">
                      {v.copy}
                    </span>
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
